import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { combineLatest } from 'rxjs';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Nav } from '../nav/nav';
import { TopHeader } from '../top-header/top-header';
import { FilterField, FilterModalComponent } from '../core/ui/filter-modal/filter-modal.component';
import { EmployeeListComponent } from './employee-list/employee-list.component';
import { AddEditEmployeeComponent, EmployeeSaveEvent } from './employee-list/add-edit-employee/add-edit-employee.component';
import { TranslatePipe } from '../core/pipes/translate.pipe';
import { AppUserService } from '../core/services';
import { AddEditPackageComponent, PackageSaveEvent } from './package-list/add-edit-package/add-edit-package.component';
import { PackageListComponent } from './package-list/package-list.component';
import { AssignPackageComponent } from './assign-package/assign-package.component';
import { SubscriptionService } from '../core/services/subscription.service';

type UserTab = 'Employee' | 'Package' | 'AssignPackage';

@Component({
  selector: 'app-user',
  standalone: true,
  imports: [CommonModule, MatSnackBarModule, Nav, TopHeader, FilterModalComponent, EmployeeListComponent, AddEditEmployeeComponent, PackageListComponent, AddEditPackageComponent, AssignPackageComponent, TranslatePipe],
  templateUrl: './user.html',
  styleUrls: ['./user.css', './../../CSSFiles/Tabs.css', './../../CSSFiles/BTN_Style.css'],
})
export class User {
  @ViewChild(EmployeeListComponent) employeeList?: EmployeeListComponent;
  @ViewChild(PackageListComponent) packageList?: PackageListComponent;
  @ViewChild(AssignPackageComponent) assignPackageList?: AssignPackageComponent;

  activeTab: UserTab = 'Employee';
  showNewEmployee = false;
  showNewPackage = false;
  showFilter = false;
  isRoleOne = false;
  employeeFilterValues: Record<string, any> = {};
  assignPackageFilterValues: Record<string, any> = {};
  employeeFilterActive = false;
  assignPackageFilterActive = false;

  readonly employeeFilterFields: FilterField[] = [
    {
      key: 'name',
      label: 'filter.employeeName',
      type: 'text',
      placeholder: 'placeholder.typeEmployeeName'
    },
    {
      key: 'mobileNumber',
      label: 'filter.mobile',
      type: 'text',
      placeholder: 'placeholder.typeMobileNumber'
    },
    {
      key: 'email',
      label: 'filter.email',
      type: 'text',
      placeholder: 'placeholder.typeEmail'
    },
    {
      key: '__showDeleted',
      label: 'common.data',
      type: 'switch',
      switchLabel: 'common.showDeletedData'
    }
  ];

  assignPackageFilterFields: FilterField[] = [
    {
      key: 'userId',
      label: 'assignPackage.user',
      type: 'select',
      placeholder: 'placeholder.selectUser',
      dropdownObj: {
        name: 'userId',
        valueKey: 'value',
        display1: 'label',
        display2: 'email',
        label: 'assignPackage.user',
        placeholder: 'placeholder.selectUser',
        isSearchable: true,
        options: []
      }
    },
    {
      key: 'packageName',
      label: 'assignPackage.package',
      type: 'select',
      placeholder: 'placeholder.selectPackage',
      dropdownObj: {
        name: 'packageName',
        valueKey: 'value',
        display1: 'label',
        label: 'assignPackage.package',
        placeholder: 'placeholder.selectPackage',
        isSearchable: true,
        searchReadonly: true,
        options: []
      }
    },
    {
      key: 'durationUnite',
      label: 'assignPackage.duration',
      type: 'select',
      placeholder: 'placeholder.selectDuration',
      options: [
        { value: 1, label: 'assignPackage.durationMonthly' },
        { value: 2, label: 'assignPackage.durationYearly' },
        { value: 3, label: 'assignPackage.durationCustom' }
      ]
    },
    {
      key: '__showDeleted',
      label: 'common.data',
      type: 'switch',
      switchLabel: 'common.showDeletedData'
    }
  ];

  constructor(
    private readonly appUsers: AppUserService,
    private readonly subscriptionService: SubscriptionService,
    private readonly snackBar: MatSnackBar
  ) {
    this.appUsers.me().subscribe({
      next: (profile) => {
        this.isRoleOne = Number(profile?.role) === 1;
      },
      error: () => {
        this.isRoleOne = false;
      }
    });

    this.loadAssignPackageFilterOptions();
  }

  selectTab(tab: UserTab): void {
    if ((tab === 'Package' || tab === 'AssignPackage') && !this.isRoleOne) {
      return;
    }
    this.activeTab = tab;
    this.showFilter = false;
  }

  openNewEmployee(): void {
    if (this.employeeList?.isCreateDisabled) {
      this.openLimitToast(this.employeeList?.createLimitMessage() || 'Subuser create limit reached.');
      return;
    }
    this.showNewEmployee = true;
  }

  openNewPackage(): void {
    if (!this.isRoleOne) {
      return;
    }
    this.showNewPackage = true;
  }

  openNewAssignPackage(): void {
    if (!this.isRoleOne) {
      return;
    }
    this.assignPackageList?.openCreateModal();
  }

  closeNewEmployee(): void {
    this.showNewEmployee = false;
  }

  closeNewPackage(): void {
    this.showNewPackage = false;
  }

  handleEmployeeSave(_: EmployeeSaveEvent): void {
    const refId = Number(_?.refId);
    const mode = _?.mode;
    this.showNewEmployee = false;
    this.employeeList?.fetchEmployees(true);
    if (mode === 'create' && Number.isFinite(refId)) {
      this.employeeList?.openUploadAfterCreate(refId);
    }
  }

  handlePackageSave(_: PackageSaveEvent): void {
    this.showNewPackage = false;
    this.packageList?.fetchPackages();
  }

  toggleFilter(): void {
    this.showFilter = !this.showFilter;
  }

  isFilterActive(): boolean {
    if (this.activeTab === 'Employee') {
      return this.employeeFilterActive;
    }
    if (this.activeTab === 'AssignPackage') {
      return this.assignPackageFilterActive;
    }
    return false;
  }

  applyFilters(values: Record<string, any>, isActive = false): void {
    if (this.activeTab === 'Employee') {
      this.showFilter = false;
      const normalized = this.normalizeFilters(values);
      this.employeeFilterValues = { ...values };
      this.employeeFilterActive = isActive;
      this.employeeList?.setFilters(normalized);
      return;
    }

    if (this.activeTab === 'AssignPackage') {
      this.showFilter = false;
      const normalized = this.normalizeFilters(values);
      this.assignPackageFilterValues = { ...values };
      this.assignPackageFilterActive = isActive;
      this.assignPackageList?.setFilters(normalized);
      return;
    }
    this.showFilter = false;
  }

  resetFilters(): void {
    if (this.activeTab === 'Employee') {
      this.employeeFilterValues = { __showDeleted: false };
      this.employeeFilterActive = false;
      this.employeeList?.setFilters({ status: 1 });
      return;
    }

    if (this.activeTab === 'AssignPackage') {
      this.assignPackageFilterValues = { __showDeleted: false };
      this.assignPackageFilterActive = false;
      this.assignPackageList?.setFilters({ status: 1 });
      return;
    }
    this.showFilter = false;
  }

  private normalizeFilters(values: Record<string, any>): Record<string, any> {
    const showDeleted = !!values?.['__showDeleted'];
    const { __showDeleted, ...rest } = values || {};
    return {
      ...rest,
      status: showDeleted ? 0 : 1
    };
  }

  private loadAssignPackageFilterOptions(): void {
    combineLatest([
      this.appUsers.listUsers({
        pagination: true,
        pageNo: 0,
        rowCount: 200,
        filters: { status: 1, role: 2 }
      }),
      this.subscriptionService.listPackages({
        pagination: true,
        pageNo: 0,
        rowCount: 200,
        filters: { status: 1 }
      })
    ]).subscribe({
      next: ([userRes, packageRes]) => {
        const userOptions = (userRes.data || []).map((user) => ({
          value: Number(user.id),
          label: `${user.organizationName ?? user.name ?? ''}`.trim() || `User #${user.id}`,
          email: `${user.email ?? ''}`.trim()
        }));

        const packageOptions = (packageRes.data || []).map((item) => ({
          value: `${item.packageName ?? ''}`.trim(),
          label: `${item.packageName ?? ''}`.trim()
        }));

        this.assignPackageFilterFields = this.assignPackageFilterFields.map((field) => {
          if (field.key === 'userId') {
            return {
              ...field,
              dropdownObj: {
                ...(field.dropdownObj ?? {}),
                options: userOptions
              }
            };
          }

          if (field.key === 'packageName') {
            return {
              ...field,
              dropdownObj: {
                ...(field.dropdownObj ?? {}),
                options: packageOptions
              }
            };
          }

          return field;
        });
      }
    });
  }

  private openLimitToast(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3500,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['dashboard-toast-warning']
    });
  }
}
